import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Collapse,
  Flex,
  Heading,
  IconButton,
  Input,
  Spinner,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { FaArrowDown, FaArrowUp, FaChevronDown, FaChevronRight, FaDownload, FaFileAlt, FaPaperclip, FaPen, FaPlus, FaTrash } from "react-icons/fa";
import { RichTextEditor, RichTextView } from "../../components/RichText";
import { ClientProcedure, ClientProcedureAttachment, MAX_ATTACHMENT_BYTES } from "../../models/Intranet";
import {
  createProcedure,
  deleteProcedure,
  deleteProcedureAttachment,
  downloadProcedureAttachment,
  getClientProcedures,
  getGeneralProcedures,
  reorderProcedures,
  updateProcedure,
  uploadProcedureAttachment,
} from "../../services/intranetService";
import { crm } from "../../theme";

export type ProcedureScope = { commercialClientId: string } | { unitType: number };

interface Props {
  scope: ProcedureScope;
  /** Avisa o pai (drawer) que há edição não salva, p/ pedir confirmação ao fechar. */
  onDirtyChange?: (dirty: boolean) => void;
  emptyText?: string;
}

interface Draft {
  id?: string;
  title: string;
  contentHtml: string;
  expectedUpdatedDate?: string | null;
}

const dateTime = (value?: string | null) =>
  value ? new Date(value).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "";

const fileSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const errorOf = (data: any, fallback: string) => (data && data.success === false ? data.notifications?.[0] ?? fallback : null);

type Confirm = { kind: "discard"; then: () => void } | { kind: "deleteProcedure"; procedure: ClientProcedure } | { kind: "deleteAttachment"; attachment: ClientProcedureAttachment };

const ProceduresPanel = ({ scope, onDirtyChange, emptyText = "Nenhum procedimento cadastrado." }: Props) => {
  const [procedures, setProcedures] = useState<ClientProcedure[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [draft, setDraft] = useState<Draft | null>(null);
  const [original, setOriginal] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [reordering, setReordering] = useState(false);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const uploadTarget = useRef<string | null>(null);
  // Rascunho mais recente (uploads em sequência leem a data já atualizada pelo anterior)
  const draftRef = useRef(draft);
  draftRef.current = draft;
  // Imagens enviadas nesta edição: as que não ficarem no texto o servidor exclui ao salvar; descartar a edição exclui todas
  const sessionImages = useRef(new Set<string>());
  // Exclusões das imagens descartadas ainda em voo: abrir edição espera por elas (mudam o UpdatedDate)
  const pendingDrop = useRef<Promise<unknown>>(Promise.resolve());
  const toast = useToast();

  const scopeKey = "commercialClientId" in scope ? `c:${scope.commercialClientId}` : `u:${scope.unitType}`;
  const dirty = draft != null && JSON.stringify(draft) !== JSON.stringify(original);

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty]);
  useEffect(() => () => onDirtyChange?.(false), []);

  const currentScope = useRef(scopeKey);
  currentScope.current = scopeKey;

  const load = async (): Promise<ClientProcedure[] | null> => {
    const requested = scopeKey;
    try {
      const list = "commercialClientId" in scope ? await getClientProcedures(scope.commercialClientId) : await getGeneralProcedures(scope.unitType);
      if (currentScope.current !== requested) return null;
      setProcedures(list);
      return list;
    } catch {
      if (currentScope.current === requested) toast({ title: "Não foi possível carregar os procedimentos.", status: "error" });
      return null;
    } finally {
      if (currentScope.current === requested) setLoading(false);
    }
  };

  /** Data que o rascunho aberto deste procedimento espera (undefined = não está em edição). */
  const draftDate = (procedureId: string) => (draftRef.current?.id === procedureId ? draftRef.current.expectedUpdatedDate : undefined);

  /**
   * Anexar/excluir anexo muda o UpdatedDate do procedimento. O servidor só devolve o novo se o rascunho estava em dia;
   * sem ele (outra pessoa salvou no meio) o rascunho mantém a data velha e o salvar avisa do conflito.
   */
  const syncDraftDate = (procedureId: string, fresh?: string | null) => {
    if (!fresh) return;
    const apply = (value: Draft | null) => (value?.id === procedureId ? { ...value, expectedUpdatedDate: fresh } : value);
    draftRef.current = apply(draftRef.current);
    setDraft(apply);
    setOriginal(apply);
  };

  /** reload = recarrega a lista depois (data/autor do card); false no unmount. */
  const dropSessionImages = (reload = true) => {
    const ids = Array.from(sessionImages.current);
    sessionImages.current.clear();
    if (ids.length === 0) return;
    const deletes = Promise.allSettled([pendingDrop.current, ...ids.map((id) => deleteProcedureAttachment(id))]);
    pendingDrop.current = deletes;
    if (reload) deletes.then(() => load());
  };

  const clearDraft = () => {
    dropSessionImages();
    // Já agora (não no próximo render): o startEdit que vem do "Descartar" compara com este valor
    draftRef.current = null;
    setDraft(null);
    setOriginal(null);
  };

  useEffect(() => {
    setLoading(true);
    setProcedures([]);
    clearDraft();
    setExpanded(new Set());
    load();
  }, [scopeKey]);

  useEffect(() => () => dropSessionImages(false), []);

  const guard = (action: () => void) => (dirty ? setConfirm({ kind: "discard", then: action }) : action());

  const startNew = () => guard(() => {
    dropSessionImages();
    const blank = { title: "", contentHtml: "" };
    setDraft(blank);
    setOriginal(blank);
  });

  // Abre pela versão atual do servidor: a lista pode estar velha (imagens descartadas, outra pessoa salvou)
  const startEdit = (procedure: ClientProcedure) => guard(async () => {
    dropSessionImages(false);
    const requested = scopeKey;
    // Rascunho na tela agora: se mudar durante a espera (digitou, "Novo"), não troca por cima sem perguntar
    const shown = draftRef.current;
    setOpeningId(procedure.id);
    try {
      await pendingDrop.current;
      const list = await load();
      if (currentScope.current !== requested) return;
      if (draftRef.current !== shown) {
        toast({ title: `"${procedure.title}" não foi aberto: o rascunho em edição mudou enquanto carregava.`, status: "info" });
        return;
      }
      const fresh = list ? list.find((p) => p.id === procedure.id) : procedure;
      if (!fresh) {
        toast({ title: "Este procedimento foi excluído.", status: "warning" });
        return;
      }
      const value = { id: fresh.id, title: fresh.title, contentHtml: fresh.contentHtml ?? "", expectedUpdatedDate: fresh.updatedDate ?? null };
      setDraft(value);
      setOriginal(value);
      setExpanded((current) => new Set(current).add(fresh.id));
    } finally {
      setOpeningId(null);
    }
  });

  const cancelEdit = () => guard(clearDraft);

  const save = async () => {
    if (!draft) return;
    if (!draft.title.trim()) {
      toast({ title: "Informe o título do procedimento.", status: "warning" });
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...scope,
        title: draft.title,
        contentHtml: draft.contentHtml || null,
        expectedUpdatedDate: draft.expectedUpdatedDate ?? null,
        discardAttachmentIds: Array.from(sessionImages.current),
      };
      const { data } = draft.id ? await updateProcedure(draft.id, body) : await createProcedure(body);
      const error = errorOf(data, "Não foi possível salvar o procedimento.");
      if (error) {
        toast({ title: error, status: "error" });
        return;
      }
      // Servidor já tratou as imagens da sessão (ficaram no texto ou foram excluídas)
      sessionImages.current.clear();
      setDraft(null);
      setOriginal(null);
      if (data?.id) setExpanded((current) => new Set(current).add(data.id));
      toast({ title: "Procedimento salvo.", status: "success" });
      await load();
    } catch {
      toast({ title: "Não foi possível salvar o procedimento.", status: "error" });
    } finally {
      setSaving(false);
    }
  };

  // Um reorder por vez: PUTs sobrepostos podem chegar fora de ordem e gravar a ordem velha
  const move = async (index: number, delta: number) => {
    if (reordering) return;
    const next = [...procedures];
    const [item] = next.splice(index, 1);
    next.splice(index + delta, 0, item);
    setProcedures(next);
    setReordering(true);
    try {
      const { data } = await reorderProcedures(next.map((p) => p.id));
      const error = errorOf(data, "Não foi possível reordenar.");
      if (error) toast({ title: error, status: "error" });
    } catch {
      toast({ title: "Não foi possível reordenar.", status: "error" });
    }
    try {
      await load();
    } finally {
      setReordering(false);
    }
  };

  const pickFiles = (procedureId: string) => {
    uploadTarget.current = procedureId;
    fileInput.current?.click();
  };

  const upload = async (files: FileList | null) => {
    const procedureId = uploadTarget.current;
    if (!procedureId || !files?.length) return;
    setUploadingId(procedureId);
    try {
      for (const file of Array.from(files)) {
        if (file.size > MAX_ATTACHMENT_BYTES) {
          toast({ title: `${file.name}: arquivo excede 25 MB.`, status: "warning" });
          continue;
        }
        try {
          const { data } = await uploadProcedureAttachment(procedureId, file, draftDate(procedureId));
          const error = errorOf(data, "Não foi possível anexar.");
          if (error) toast({ title: `${file.name}: ${error}`, status: "error" });
          else syncDraftDate(procedureId, data?.procedureUpdatedDate);
        } catch {
          toast({ title: `${file.name}: não foi possível anexar.`, status: "error" });
        }
      }
      await load();
    } finally {
      setUploadingId(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  // Imagem colada/escolhida no editor: vira anexo do procedimento em edição e entra no texto pelo id
  const uploadImage = async (file: File): Promise<string | null> => {
    const procedureId = draft?.id;
    if (!procedureId) return null;
    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast({ title: `${file.name}: arquivo excede 25 MB.`, status: "warning" });
      return null;
    }
    try {
      const { data } = await uploadProcedureAttachment(procedureId, file, draftDate(procedureId));
      const error = errorOf(data, "Não foi possível enviar a imagem.");
      if (error) {
        toast({ title: error, status: "error" });
        return null;
      }
      // Edição fechada enquanto enviava: ninguém vai pôr a imagem no texto, exclui já (senão fica anexo solto)
      if (draftRef.current?.id !== procedureId) {
        if (data?.id) deleteProcedureAttachment(data.id).catch(() => undefined);
        return null;
      }
      // Sem recarregar a lista: o card deste procedimento está trocado pelo editor; recarrega ao salvar
      syncDraftDate(procedureId, data?.procedureUpdatedDate);
      if (data?.id) sessionImages.current.add(data.id);
      return data?.id ?? null;
    } catch {
      toast({ title: "Não foi possível enviar a imagem.", status: "error" });
      return null;
    }
  };

  const download = async (attachment: ClientProcedureAttachment) => {
    try {
      await downloadProcedureAttachment(attachment.id, attachment.fileName);
    } catch {
      toast({ title: "Não foi possível baixar o arquivo.", status: "error" });
    }
  };

  const runConfirm = async () => {
    const current = confirm;
    setConfirm(null);
    if (!current) return;
    if (current.kind === "discard") {
      clearDraft();
      current.then();
      return;
    }
    try {
      const owner = current.kind === "deleteAttachment" ? procedures.find((p) => p.attachments.some((a) => a.id === current.attachment.id)) : undefined;
      const { data } = current.kind === "deleteProcedure"
        ? await deleteProcedure(current.procedure.id)
        : await deleteProcedureAttachment(current.attachment.id, owner ? draftDate(owner.id) : undefined);
      const error = errorOf(data, "Não foi possível excluir.");
      if (error) toast({ title: error, status: "error" });
      else if (owner) syncDraftDate(owner.id, data?.procedureUpdatedDate);
      if (current.kind === "deleteProcedure" && draft?.id === current.procedure.id) {
        // Sem o reload do dropSessionImages: o load abaixo já recarrega
        dropSessionImages(false);
        setDraft(null);
        setOriginal(null);
      }
      await load();
    } catch {
      toast({ title: "Não foi possível excluir.", status: "error" });
    }
  };

  const toggle = (id: string) => setExpanded((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const editor = (
    <Box border="1px solid" borderColor={crm.brand} borderRadius={crm.radius} p="14px" bg={crm.surface} boxShadow={crm.shadowSm}>
      <Input
        value={draft?.title ?? ""}
        maxLength={200}
        placeholder="Título (ex.: Instrução de embarque, Faturamento)"
        fontWeight="700"
        mb="10px"
        onChange={(e) => setDraft((current) => current && { ...current, title: e.target.value })}
      />
      <RichTextEditor value={draft?.contentHtml} onChange={(html) => setDraft((current) => current && { ...current, contentHtml: html })} onUploadImage={draft?.id ? uploadImage : undefined} />
      {draft && !draft.id && <Text fontSize="xs" color={crm.textFaint} mt="6px">Para colocar imagens no texto, salve o procedimento e edite de novo.</Text>}
      <Flex justify="flex-end" gap="8px" mt="10px">
        <Button size="sm" variant="ghost" onClick={cancelEdit}>Cancelar</Button>
        <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving}>Salvar</Button>
      </Flex>
    </Box>
  );

  return (
    <Box>
      <input ref={fileInput} type="file" multiple hidden onChange={(e) => upload(e.target.files)} />
      <Flex justify="space-between" align="center" mb="12px">
        <Heading size="sm" color={crm.text}>Procedimentos</Heading>
        <Button size="sm" leftIcon={<FaPlus />} variant="outline" borderColor={crm.borderStrong} onClick={startNew} isDisabled={draft != null && !draft.id}>Novo procedimento</Button>
      </Flex>

      {loading && <Flex py="24px" justify="center"><Spinner /></Flex>}
      {!loading && procedures.length === 0 && !draft && <Text fontSize="sm" color={crm.textFaint} py="10px">{emptyText}</Text>}

      {draft && !draft.id && <Box mb="10px">{editor}</Box>}

      {procedures.map((procedure, index) => {
        if (draft?.id === procedure.id) return <Box key={procedure.id} mb="10px">{editor}</Box>;
        const open = expanded.has(procedure.id);
        const files = procedure.attachments.filter((attachment) => !attachment.inline);
        const images = procedure.attachments.length - files.length;
        return (
          <Box key={procedure.id} mb="8px" border="1px solid" borderColor={crm.border} borderRadius={crm.radius} bg={crm.surface} boxShadow={crm.shadowSm}>
            <Flex align="center" gap="10px" px="12px" py="10px" cursor="pointer" onClick={() => toggle(procedure.id)} _hover={{ bg: crm.subtle }} borderTopRadius={crm.radius} borderBottomRadius={open ? 0 : crm.radius}>
              <Box color={crm.textMuted} fontSize="xs">{open ? <FaChevronDown /> : <FaChevronRight />}</Box>
              <Box color={crm.brand}><FaFileAlt /></Box>
              <Box flex="1" minW="0">
                <Text fontWeight="700" fontSize="sm" noOfLines={1}>{procedure.title}</Text>
                <Text fontSize="xs" color={crm.textMuted}>
                  {procedure.updatedByName ? `${procedure.updatedByName} · ` : ""}{dateTime(procedure.updatedDate ?? procedure.createdDate)}
                  {files.length > 0 && ` · ${files.length} anexo${files.length > 1 ? "s" : ""}`}
                  {images > 0 && ` · ${images} image${images > 1 ? "ns" : "m"}`}
                </Text>
              </Box>
              <Flex gap="2px" onClick={(e) => e.stopPropagation()}>
                <Tooltip label="Subir"><IconButton aria-label="Subir" size="xs" variant="ghost" icon={<FaArrowUp />} isDisabled={index === 0 || reordering} onClick={() => move(index, -1)} /></Tooltip>
                <Tooltip label="Descer"><IconButton aria-label="Descer" size="xs" variant="ghost" icon={<FaArrowDown />} isDisabled={index === procedures.length - 1 || reordering} onClick={() => move(index, 1)} /></Tooltip>
                <Tooltip label="Editar"><IconButton aria-label="Editar" size="xs" variant="ghost" icon={<FaPen />} isLoading={openingId === procedure.id} isDisabled={openingId != null} onClick={() => startEdit(procedure)} /></Tooltip>
                <Tooltip label="Excluir"><IconButton aria-label="Excluir" size="xs" variant="ghost" color={crm.danger} icon={<FaTrash />} onClick={() => setConfirm({ kind: "deleteProcedure", procedure })} /></Tooltip>
              </Flex>
            </Flex>
            <Collapse in={open} animateOpacity>
              <Box px="16px" pb="14px" pt="4px" borderTop="1px solid" borderColor={crm.border}>
                <Box pt="8px"><RichTextView html={procedure.contentHtml} /></Box>
                <Box mt="14px">
                  <Flex align="center" justify="space-between" mb="6px">
                    <Text fontSize="xs" fontWeight="600" color={crm.textMuted}>ANEXOS</Text>
                    <Button size="xs" variant="ghost" leftIcon={<FaPaperclip />} onClick={() => pickFiles(procedure.id)} isLoading={uploadingId === procedure.id} loadingText="Enviando">Anexar arquivo</Button>
                  </Flex>
                  {files.length === 0 && <Text fontSize="xs" color={crm.textFaint}>Nenhum arquivo.</Text>}
                  {files.map((attachment) => (
                    <Flex key={attachment.id} align="center" gap="8px" py="5px" px="8px" borderRadius={crm.radiusSm} _hover={{ bg: crm.subtle }}>
                      <Box color={crm.textMuted}><FaPaperclip /></Box>
                      <Button variant="link" size="sm" color={crm.brand} fontWeight="500" onClick={() => download(attachment)} noOfLines={1} textAlign="left" whiteSpace="normal" wordBreak="break-all">{attachment.fileName}</Button>
                      <Text fontSize="xs" color={crm.textFaint} flexShrink={0}>{fileSize(attachment.size)}</Text>
                      <Box flex="1" />
                      <Tooltip label="Baixar"><IconButton aria-label="Baixar" size="xs" variant="ghost" icon={<FaDownload />} onClick={() => download(attachment)} /></Tooltip>
                      <Tooltip label="Remover anexo"><IconButton aria-label="Remover anexo" size="xs" variant="ghost" color={crm.danger} icon={<FaTrash />} onClick={() => setConfirm({ kind: "deleteAttachment", attachment })} /></Tooltip>
                    </Flex>
                  ))}
                </Box>
              </Box>
            </Collapse>
          </Box>
        );
      })}

      <AlertDialog isOpen={confirm != null} leastDestructiveRef={cancelRef} onClose={() => setConfirm(null)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px" boxShadow={crm.shadowLg}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {confirm?.kind === "discard" ? "Descartar alterações?" : confirm?.kind === "deleteProcedure" ? "Excluir procedimento?" : "Remover anexo?"}
            </AlertDialogHeader>
            <AlertDialogBody>
              {confirm?.kind === "discard" && "O procedimento em edição tem alterações não salvas. Se continuar, elas serão perdidas."}
              {confirm?.kind === "deleteProcedure" && `"${confirm.procedure.title}" e seus anexos saem da ficha.`}
              {confirm?.kind === "deleteAttachment" && `"${confirm.attachment.fileName}" sai do procedimento.`}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" onClick={() => setConfirm(null)}>{confirm?.kind === "discard" ? "Continuar editando" : "Cancelar"}</Button>
              <Button colorScheme="red" ml={3} onClick={runConfirm}>{confirm?.kind === "discard" ? "Descartar" : "Excluir"}</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ProceduresPanel;
